import { useEffect, useState } from "react";
import { useIntercomStore } from "@utils/stores/intercomStore";

/**
 * The info of the user stored locally.
 */
interface PersonalInfo {
  firstName?: string;
  lastName?: string;
  userId?: string;
  email?: string;
  role?: string;
}

interface LiteHomeInit {
  /**
   * The users display name.
   */
  displayName: string | undefined;
  /**
   * Whether intercom has been initialized.
   */
  isIntercomInitialized: boolean;
}

/**
 * Custom hook that initializes Intercom with lite app ID using personal info from localStorage
 * and manages display name state
 */
export const useLiteHomeInit = (): LiteHomeInit => {
  const [displayName, setDisplayName] = useState<string | undefined>(undefined);
  const { initialize, showIntercom, isInitialized } = useIntercomStore();

  /**
   * Initialize Intercom with the lite app ID on component mount.
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Initialize Intercom
    initialize().then((success) => {
      // If "support" is in the URL query params and initialization succeeded, open Intercom messenger
      if (success && urlParams.has("support")) {
        showIntercom();
      }
    });
  }, [initialize, showIntercom]);

  useEffect(() => {
    try {
      let personalInfoStr =
        localStorage.getItem("personalInfo_client") ||
        localStorage.getItem("personalInfo");
      if (personalInfoStr) {
        const personalInfo = JSON.parse(personalInfoStr) as PersonalInfo;
        const fullName = [personalInfo.firstName, personalInfo.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        setDisplayName(fullName || undefined);

        // Initialize Intercom with lite app ID if we have user info and haven't initialized yet
        if (!isInitialized) {
          initialize();
        }
      }
    } catch {
      setDisplayName(undefined);
    }
  }, [isInitialized, initialize]);

  return {
    displayName,
    isIntercomInitialized: isInitialized,
  };
};
